import { ClassNames } from '@emotion/react'
import {
    BreakpointRatios,
    FixedRatio,
    ImageSource,
    RenderTargetContext,
    scaleImage,
} from '@news-mono/web-common'
import React, { useContext } from 'react'
import { pictureLayouts } from './layouts/layouts'
import { ImageSrcInfo, LazyLoadingPicture } from './LazyLoadingPicture'
import { ResponsivePictureSizes } from './responsive'
import { ResponsivePictureLayout } from './ResponsivePictureLayouts'
import { fixedRatioToBreakpointRatios, getCrops } from './utils'

export interface ResponsivePictureProps {
    layout: ResponsivePictureLayout
    className?: string
    disableLazyLoad?: boolean
    image: ImageSource
    imageWidths: ResponsivePictureSizes
    fixedRatio: FixedRatio | BreakpointRatios
    /**
     * Allows the selection of the original crop if it matches the
     * requested fixed ratio
     **/
    allowOriginalCrop?: boolean
    renderPicture?: (pic: React.ReactElement) => React.ReactElement
}

export const WebPicture: React.FC<ResponsivePictureProps> = ({
    className,
    fixedRatio,
    image,
    imageWidths,
    renderPicture = (pic) => pic,
    layout,
    disableLazyLoad,
    allowOriginalCrop = false,
}) => {
    const scaledImage: ImageSrcInfo = {
        srcCallback: scaleImage,
        initialWidth: imageWidths.fallbackWidth,
    }

    const breakpointRatios = fixedRatioToBreakpointRatios(fixedRatio)
    const breakpointCrops = getCrops(
        image?.crops,
        breakpointRatios,
        allowOriginalCrop,
    )
    const { renderTarget } = useContext(RenderTargetContext)

    if (!breakpointCrops) {
        return null
    }

    const layoutPictureStyles = pictureLayouts[layout].pictureStyles()

    return renderPicture(
        <ClassNames>
            {({ css, cx }) => (
                <LazyLoadingPicture
                    pictureClassName={cx(css(layoutPictureStyles), className)}
                    fixedRatio={fixedRatio}
                    image={image}
                    imageSizes={imageWidths}
                    srcInfo={scaledImage}
                    disableLazyLoad={
                        disableLazyLoad || renderTarget === 'preview'
                    }
                    breakpointCrops={breakpointCrops}
                />
            )}
        </ClassNames>,
    )
}

WebPicture.displayName = 'WebPicture'
